import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Pencil, Sparkles, Search, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Skills() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState({ open: false, editing: null });
  const empty = { name: "", category: "", description: "", body: "", is_shared: true };
  const [form, setForm] = useState(empty);

  const canEdit = user?.role === "admin" || user?.role === "abogado";

  const load = async () => {
    try {
      const { data } = await api.get("/skills");
      setItems(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (dlg.editing) {
        await api.patch(`/skills/${dlg.editing.id}`, form);
        toast.success("Skill actualizada");
      } else {
        await api.post("/skills", form);
        toast.success("Skill creada");
      }
      setDlg({ open: false, editing: null });
      setForm(empty);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta skill?")) return;
    try {
      await api.delete(`/skills/${id}`);
      toast.success("Eliminada");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = (s) => {
    setForm({
      name: s.name, category: s.category || "", description: s.description || "",
      body: s.body, is_shared: !!s.is_shared,
    });
    setDlg({ open: true, editing: s });
  };

  const openNew = () => {
    setForm(empty);
    setDlg({ open: true, editing: null });
  };

  const filtered = items.filter((s) => {
    if (!q.trim()) return true;
    const n = q.toLowerCase();
    return [s.name, s.category || "", s.description || ""].join(" ").toLowerCase().includes(n);
  });

  return (
    <PageContainer>
      <PageHeader
        title="Skills / Instrucciones IA"
        subtitle="Plantillas de instrucciones para la IA (markdown). Úsalas en el Generador para no tener que explicar cada vez cómo debe redactar."
        actions={
          canEdit && (
            <Button onClick={openNew} data-testid="new-skill-button" className="rounded-sm bg-[#7B1826] hover:bg-[#5D121D]">
              <Plus className="w-4 h-4 mr-2" />Nueva skill
            </Button>
          )
        }
      />

      <div className="bg-white border border-[#E5E7EB] rounded-sm p-3 mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, categoría…" className="rounded-sm pl-9 h-9" data-testid="skills-search" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#E5E7EB] bg-white">
          <BookOpen className="w-10 h-10 mx-auto text-neutral-400" />
          <p className="mt-4 text-sm text-neutral-500">Sin skills todavía.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((s) => (
            <div key={s.id} data-testid={`skill-card-${s.id}`} className="bg-white border border-[#E5E7EB] rounded-sm p-4 hover:border-[#7B1826]/40 transition-colors flex flex-col">
              <div className="flex items-start gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#7B1826] shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[#0F172A] truncate">{s.name}</div>
                  {s.category && (
                    <Badge variant="outline" className="rounded-sm text-[10px] mt-1 border-[#E5E7EB]">{s.category}</Badge>
                  )}
                </div>
                {s.usage_count > 0 && (
                  <span className="text-[10px] text-neutral-500 font-mono">×{s.usage_count}</span>
                )}
              </div>
              {s.description && (
                <p className="text-xs text-neutral-600 mb-2 line-clamp-2">{s.description}</p>
              )}
              <div className="flex-1 text-[11px] text-neutral-500 font-mono bg-[#FBFBF9] border border-[#E5E7EB] rounded-sm p-2 max-h-20 overflow-hidden">
                {(s.body || "").slice(0, 220)}{(s.body?.length || 0) > 220 ? "…" : ""}
              </div>
              {canEdit && (
                <div className="flex items-center justify-end gap-1 mt-3 pt-2 border-t border-[#E5E7EB]">
                  <button onClick={() => openEdit(s)} className="p-1.5 text-neutral-500 hover:text-[#7B1826]" data-testid={`skill-edit-${s.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(s.id)} className="p-1.5 text-neutral-500 hover:text-[#DC2626]" data-testid={`skill-delete-${s.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={dlg.open} onOpenChange={(v) => setDlg({ open: v, editing: v ? dlg.editing : null })}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="skill-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {dlg.editing ? "Editar skill" : "Nueva skill"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" data-testid="skill-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Categoría</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ej. Civil · Bancario" className="rounded-sm mt-1" />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-xs text-neutral-600 cursor-pointer">
                  <Checkbox checked={form.is_shared} onCheckedChange={(v) => setForm({ ...form, is_shared: !!v })} data-testid="skill-shared" />
                  Visible para todo el equipo
                </label>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Descripción breve</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Qué hace esta skill" className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Instrucciones para la IA (Markdown)</Label>
              <Textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder={"Indícale a la IA cómo redactar: estructura, tono, normativa a citar, límites..."}
                className="rounded-sm mt-1 font-mono text-xs"
                rows={14}
                data-testid="skill-body"
              />
              <p className="text-[11px] text-neutral-500 mt-1">
                Consejo: describe la estructura (HECHOS, FUNDAMENTOS, SUPLICO…), el tono y los artículos de referencia.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg({ open: false, editing: null })} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} disabled={!form.name || !form.body} data-testid="skill-save" className="rounded-sm bg-[#7B1826] hover:bg-[#5D121D]">
              {dlg.editing ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
